import { useEffect, useState } from "react";
import { Link, NavLink, Outlet, useNavigate } from "react-router-dom";
import { Search, Bell, Mail, Shield, LogOut, User, Settings, ArrowLeft, Menu, X } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { ASSETS } from "@/data/content";
import { Avatar, RoleBadge } from "./ui";

const linkCls = ({ isActive }) =>
  `rounded-full px-3 py-1.5 text-[13px] font-medium transition-colors ${isActive ? "bg-white/10 text-white" : "text-white/60 hover:text-white"}`;

const Badge = ({ n }) => n > 0 && (
  <span className="absolute -right-1 -top-1 grid h-4 min-w-4 place-items-center rounded-full bg-[var(--glow)] px-1 font-mono text-[9px] font-bold text-black">{n}</span>
);

export default function ForumLayout() {
  const { user, logout, level } = useAuth();
  const [q, setQ] = useState("");
  const [menu, setMenu] = useState(false);
  const [mobile, setMobile] = useState(false);
  const nav = useNavigate();

  useEffect(() => { window.scrollTo(0, 0); }, []);

  const search = (e) => {
    e.preventDefault();
    if (q.trim().length >= 2) nav(`/forum/search?q=${encodeURIComponent(q.trim())}`);
  };

  return (
    <div className="min-h-screen bg-[#0a0a0b] text-white" data-testid="forum-layout">
      <header className="sticky top-0 z-40 border-b border-white/[0.06] bg-[#0a0a0b]/80 backdrop-blur-xl">
        <div className="mx-auto flex max-w-[1240px] items-center gap-4 px-5 py-3 sm:px-8">
          <Link to="/" className="hidden items-center gap-2 text-white/50 transition-colors hover:text-white md:flex" data-testid="forum-back-home">
            <ArrowLeft size={14} /><span className="font-mono text-[11px] tracking-widest">FŐOLDAL</span>
          </Link>
          <Link to="/forum" className="flex items-center gap-3" data-testid="forum-logo">
            <img src={ASSETS.logo} alt="BCRP" className="h-8 w-8" />
            <span className="font-display text-sm font-bold uppercase tracking-[0.18em]">Fórum</span>
          </Link>
          <nav className="ml-4 hidden items-center gap-1 md:flex" data-testid="forum-nav">
            <NavLink to="/forum" end className={linkCls} data-testid="forum-nav-home">Kategóriák</NavLink>
            <NavLink to="/ucp" className={linkCls} data-testid="forum-nav-ucp">UCP</NavLink>
            {user && <NavLink to="/forum/messages" className={linkCls} data-testid="forum-nav-messages">Üzenetek</NavLink>}
            {level >= 2 && <NavLink to="/forum/admin" className={linkCls} data-testid="forum-nav-admin">Admin</NavLink>}
          </nav>
          <form onSubmit={search} className="ml-auto hidden items-center gap-2 rounded-full border border-white/10 bg-white/[0.03] px-3 py-1.5 sm:flex" data-testid="forum-search-form">
            <Search size={14} className="text-white/40" />
            <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Keresés…" className="w-36 bg-transparent text-sm outline-none placeholder:text-white/30 lg:w-56" data-testid="forum-search-input" />
          </form>
          {user ? (
            <div className="relative flex items-center gap-2">
              <Link to="/forum/notifications" className="relative grid h-9 w-9 place-items-center rounded-full border border-white/10 text-white/70 hover:text-white" data-testid="forum-notifications-link" aria-label="Értesítések">
                <Bell size={15} /><Badge n={user.unread_notifications} />
              </Link>
              <Link to="/forum/messages" className="relative grid h-9 w-9 place-items-center rounded-full border border-white/10 text-white/70 hover:text-white" data-testid="forum-messages-link" aria-label="Üzenetek">
                <Mail size={15} /><Badge n={user.unread_messages} />
              </Link>
              <button type="button" onClick={() => setMenu((m) => !m)} className="flex items-center gap-2 rounded-full border border-white/10 py-1 pl-1 pr-3 hover:bg-white/5" data-testid="forum-user-menu">
                <Avatar user={user} size="sm" /><span className="hidden text-sm font-medium sm:inline">{user.username}</span>
              </button>
              {menu && (
                <div className="absolute right-0 top-12 w-56 overflow-hidden rounded-2xl border border-white/10 bg-[#111113] p-2 shadow-2xl" data-testid="forum-user-dropdown" onMouseLeave={() => setMenu(false)}>
                  <div className="px-3 py-2"><RoleBadge role={user.role} label={user.role_label} /></div>
                  <Link to={`/forum/u/${user.username}`} onClick={() => setMenu(false)} className="flex items-center gap-2 rounded-xl px-3 py-2 text-sm text-white/80 hover:bg-white/5" data-testid="menu-profile"><User size={14} />Profilom</Link>
                  <Link to="/forum/settings" onClick={() => setMenu(false)} className="flex items-center gap-2 rounded-xl px-3 py-2 text-sm text-white/80 hover:bg-white/5" data-testid="menu-settings"><Settings size={14} />Beállítások</Link>
                  <Link to="/ucp" onClick={() => setMenu(false)} className="flex items-center gap-2 rounded-xl px-3 py-2 text-sm text-white/80 hover:bg-white/5" data-testid="menu-ucp"><User size={14} />Játékos vezérlőpult</Link>
                  {level >= 2 && <Link to="/forum/admin" onClick={() => setMenu(false)} className="flex items-center gap-2 rounded-xl px-3 py-2 text-sm text-white/80 hover:bg-white/5" data-testid="menu-admin"><Shield size={14} />Adminisztráció</Link>}
                  <button type="button" onClick={() => { setMenu(false); logout(); nav("/forum"); }} className="flex w-full items-center gap-2 rounded-xl px-3 py-2 text-left text-sm text-red-300 hover:bg-red-500/10" data-testid="menu-logout"><LogOut size={14} />Kijelentkezés</button>
                </div>
              )}
            </div>
          ) : user === false ? (
            <div className="flex items-center gap-2">
              <Link to="/login" className="rounded-full border border-white/15 px-4 py-2 text-[13px] font-semibold text-white hover:bg-white/5" data-testid="forum-login-link">Belépés</Link>
              <Link to="/register" className="btn-glow rounded-full bg-white px-4 py-2 text-[13px] font-semibold text-black" data-testid="forum-register-link">Regisztráció</Link>
            </div>
          ) : <div className="h-9 w-24 animate-pulse rounded-full bg-white/5" />}
          <button type="button" onClick={() => setMobile((m) => !m)} className="grid h-9 w-9 place-items-center rounded-full border border-white/10 md:hidden" data-testid="forum-mobile-toggle" aria-label="Menü">
            {mobile ? <X size={16} /> : <Menu size={16} />}
          </button>
        </div>
        {mobile && (
          <div className="border-t border-white/[0.06] px-5 py-3 md:hidden" data-testid="forum-mobile-nav">
            <form onSubmit={search} className="mb-3 flex items-center gap-2 rounded-full border border-white/10 px-3 py-2 sm:hidden">
              <Search size={14} className="text-white/40" />
              <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Keresés…" className="w-full bg-transparent text-sm outline-none" />
            </form>
            <div className="flex flex-wrap gap-2" onClick={() => setMobile(false)}>
              <NavLink to="/" className={linkCls}>Főoldal</NavLink>
              <NavLink to="/forum" end className={linkCls}>Kategóriák</NavLink>
              <NavLink to="/ucp" className={linkCls}>UCP</NavLink>
              {user && <NavLink to="/forum/messages" className={linkCls}>Üzenetek</NavLink>}
              {level >= 2 && <NavLink to="/forum/admin" className={linkCls}>Admin</NavLink>}
            </div>
          </div>
        )}
      </header>
      <main className="mx-auto max-w-[1240px] px-5 py-10 sm:px-8">
        <Outlet />
      </main>
      <footer className="border-t border-white/[0.06] py-8 text-center font-mono text-[11px] text-white/30">
        © 2026 Bone County Roleplay - Minden jog fenntartva!
      </footer>
    </div>
  );
}
